import { createFileRoute } from "@tanstack/react-router";
import {
	UpdateRecordButtonDeck,
	useRecordsDeck,
	type DeckIdentifiable,
} from ":entities/deck";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
} from "@simurgh/feathers/ui/card";
import { CreateRecordButtonDeck } from ":entities/deck";
import { AnimatePresence, motion } from "motion/react";
import { isEmpty, isNil } from "lodash-es";
import { Image } from "@simurgh/feathers/ui/image";
import { Link } from "@tanstack/react-router";
import { DeleteDeckButton } from ":widgets/deck/ui/delete-deck-button";
import { useOptimistic } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/$locale/dashboard/")({
	component: DashboardIndexComponent,
});

function DashboardIndexComponent() {
	const { data: decks = [], isFetching } = useRecordsDeck();
	const { t } = useTranslation();
	const { locale } = Route.useParams();

	// Optimistic updates for deck creation
	const [optimisticDecks, addOptimisticDeck] = useOptimistic(
		decks,
		(state, newDeck: DeckIdentifiable) => [...state, newDeck],
	);

	const displayDecks = optimisticDecks;

	return (
		<>
			<title>
				{t("pages.dashboard.title")} - {t("app.name")}
			</title>
			<meta name="description" content={t("pages.dashboard.description")} />
			<section>
				<div className="flex items-center justify-between">
					<h2>{t("pages.dashboard.title")}</h2>
					<CreateRecordButtonDeck onOptimisticCreate={addOptimisticDeck} />
				</div>
				<ul className="flex flex-col py-4 gap-4">
					{isFetching ? (
						<>
							<li className="w-full">
								<Card className="shadow-sm">
									<CardHeader>
										<div className="h-5 w-40 bg-foreground/10 rounded animate-pulse" />
									</CardHeader>
									<CardFooter className="justify-end gap-2">
										<div className="h-8 w-24 bg-foreground/10 rounded animate-pulse" />
										<div className="h-8 w-24 bg-foreground/10 rounded animate-pulse" />
									</CardFooter>
								</Card>
							</li>
							<li className="w-full">
								<Card className="shadow-sm">
									<CardHeader>
										<div className="h-5 w-56 bg-foreground/10 rounded animate-pulse" />
									</CardHeader>
									<CardFooter className="justify-end gap-2">
										<div className="h-8 w-24 bg-foreground/10 rounded animate-pulse" />
										<div className="h-8 w-24 bg-foreground/10 rounded animate-pulse" />
									</CardFooter>
								</Card>
							</li>
						</>
					) : null}
					{isEmpty(displayDecks) && !isFetching ? (
						<li className="w-full">
							<Card className="shadow-sm">
								<CardContent className="justify-center">
									<div className="flex flex-col items-center justify-center gap-4 py-4">
										<Image
											src="/assets/images/branding/butterfly-2.png"
											alt="Friendly illustration showing an empty state"
											width={200}
											height={200}
											loading="lazy"
										/>
										<div className="space-y-1 text-center">
											<h3 className="text-lg font-semibold">
												{t("pages.dashboard.noDeck.title")}
											</h3>
											<p className="text-sm text-foreground/60">
												{t("pages.dashboard.noDeck.description")}
											</p>
										</div>
									</div>
								</CardContent>
								<CardFooter className="justify-center gap-2">
									<CreateRecordButtonDeck />
								</CardFooter>
							</Card>
						</li>
					) : null}
					{!isEmpty(displayDecks) && !isNil(displayDecks) ? (
						<AnimatePresence mode="sync">
							{displayDecks.map((deck) => (
								<motion.li
									key={deck.id}
									className="w-full relative"
									initial={{ scale: 0.8, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									exit={{ scale: 0.8, opacity: 0 }}
									transition={{ type: "spring" }}
								>
									<Card className="shadow-sm">
										<CardHeader>{deck.title}</CardHeader>
										<CardFooter className="justify-end">
											<UpdateRecordButtonDeck
												className=" relative z-20"
												record={deck}
												recordId={deck.id}
											/>
											<DeleteDeckButton
												className=" relative z-20"
												deckId={deck.id}
											/>
										</CardFooter>
									</Card>
									<Link
										className="absolute inset-0 z-10"
										to="/$locale/dashboard/decks/$deckId"
										params={{ deckId: deck.id, locale }}
										aria-label={t("actions.viewDeck")}
									/>
								</motion.li>
							))}
						</AnimatePresence>
					) : null}
				</ul>
			</section>
		</>
	);
}
